import { Flex, Icon, Input, PopoverTextArea, Text } from "@repo/ui";
import { ActivityType, TaskStatus } from "@repo/api/client";
import { tag } from "@repo/utils/typeUtils";
import { useLoggedInUser } from "../contexts/AuthContext";
import { useSetTaskAssignee } from "../hooks/useSetTaskAssignee";
import { useEntity, useTransaction } from "../db/DatabaseProvider";
import { ActivityLogItemId, Task, TaskId, SpecialtyId } from "../db/schema";
import { ActivityLog } from "./ActivityLog";
import { AssigneePicker } from "./AssigneePicker";
import { DepartmentPicker } from "./DepartmentPicker";
import { SpecialtyPicker } from "./SpecialtyPicker";

// import { RichTextEditor } from "./editor/RichTextEditor";
import { PatientCard } from "./PatientCard";
import { SentStatusPicker } from "./SentStatusPicker";
import { StatusPicker } from "./StatusPicker";

type TaskNotesProps = {
	taskId: TaskId;
};

/**
 * We're re-using currentStep for notes because it's already in the database and
 * we don't want to change the schema (notes is a json that represents editor state)
 */
const TaskNotes: React.FC<TaskNotesProps> = ({ taskId }) => {
	const task = useEntity("task", taskId);
	const setNotes = useTransaction((tx, notes) => {
		tx.update("task", taskId, (task) => ({ ...task, currentStep: notes }));
	});

	if (!task) return null;

	return (
		<PopoverTextArea
			onSave={setNotes}
			value={task.currentStep}
			placeholder="Notes"
			style={{ flex: 1, minWidth: 0 }}
		/>
	);
};

// type PatientNotesProps = {
// 	taskId: TaskId;
// };

// const PatientNotes: React.FC<PatientNotesProps> = ({ taskId }) => {
// 	const task = useEntity("task", taskId);
// 	const patient = useEntity("patient", task?.patient || tag(""));

// 	const setPatientNotes = useTransaction((tx, notes: any) => {
// 		if (!task?.patient) return;
// 		tx.update("patient", task.patient, (patient) => ({
// 			...patient,
// 			notes,
// 		}));
// 	});

// 	const dispatchTransaction = useCallback(
// 		function (this: EditorView, tr: Transaction) {
// 			setPatientNotes(this.state.apply(tr));
// 		},
// 		[setPatientNotes],
// 	);

// 	if (!task || !patient) return null;

// 	return (
// 		<Flex column gap="xs">
// 			<Text size="md" weight="semibold" color="sand11">
// 				Patient Notes
// 			</Text>
// 			<Editor
// 				state={patient.notes}
// 				dispatchTransaction={dispatchTransaction}
// 				style={{ minHeight: 80 }}
// 			/>
// 		</Flex>
// 	);
// };

type TaskAttributesProps = {
	taskId: TaskId;
};

const TaskAttributes: React.FC<TaskAttributesProps> = ({ taskId }) => {
	const task = useEntity("task", taskId);

	const updateTaskDepartment = useTransaction((tx, department: string) => {
		tx.update("task", taskId, (task) => ({
			...task,
			department,
		}));
	});
	const updateTaskAssignee = useSetTaskAssignee(taskId);

	const updateTaskReason = useTransaction((tx, reason: string) => {
		tx.update("task", taskId, (task) => {
			if (!task.linkedTaskParameters) return task;
			return {
				...task,
				linkedTaskParameters: {
					...task.linkedTaskParameters,
					context: reason,
				},
			};
		});
	});

	const updateTaskDiagnosisCodes = useTransaction(
		(tx, diagnosisCodes: string) => {
			tx.update("task", taskId, (task) => {
				if (!task.linkedTaskParameters) return task;
				return {
					...task,
					linkedTaskParameters: {
						...task.linkedTaskParameters,
						diagnosisCodes: diagnosisCodes
							? diagnosisCodes
									.split(",")
									.map((code) => code.trim())
									.filter((code) => code.length > 0)
							: [],
					},
				};
			});
		},
	);

	if (!task) return null;

	return (
		<div
			style={{
				display: "grid",
				gridTemplateColumns: "auto 1fr",
				gap: "8px",
				alignItems: "start",
				marginTop: "8px",
			}}
		>
			<Flex align="center" gap="xs" padding="xs">
				<Icon name="Building" size="sm" color="sand11" shrink={0} />
				<Text
					size="sm"
					weight="medium"
					color="sand11"
					style={{ whiteSpace: "nowrap" }}
				>
					Department
				</Text>
			</Flex>
			<DepartmentPicker
				department={task.department}
				onChange={updateTaskDepartment}
			/>
			<Flex align="center" gap="xs" padding="xs">
				<Icon name="UserPen" size="sm" color="sand11" shrink={0} />
				<Text
					size="sm"
					weight="medium"
					color="sand11"
					style={{ whiteSpace: "nowrap" }}
				>
					Assignee
				</Text>
			</Flex>
			<AssigneePicker
				assignedTo={task.assignedTo}
				assigneeType={task.assigneeType}
				taskId={taskId}
				onChange={updateTaskAssignee}
			/>
			<Flex align="center" gap="xs" padding="xs">
				<Icon name="NotebookPen" size="sm" color="sand11" shrink={0} />
				<Text
					size="sm"
					weight="medium"
					color="sand11"
					style={{ whiteSpace: "nowrap" }}
				>
					Notes
				</Text>
			</Flex>
			<TaskNotes taskId={taskId} />

			<Flex align="center" gap="xs" padding="xs">
				<Icon name="FileText" size="sm" color="sand11" shrink={0} />
				<Text
					size="sm"
					weight="medium"
					color="sand11"
					style={{ whiteSpace: "nowrap" }}
				>
					Reason
				</Text>
			</Flex>
			<PopoverTextArea
				value={task.linkedTaskParameters?.context ?? ""}
				onSave={updateTaskReason}
				placeholder="Reason"
				style={{ flex: 1, minWidth: 0 }}
			/>

			<Flex align="center" gap="xs" padding="xs">
				<Icon name="Stethoscope" size="sm" color="sand11" shrink={0} />
				<Text
					size="sm"
					weight="medium"
					color="sand11"
					style={{ whiteSpace: "nowrap" }}
				>
					Diagnosis Codes
				</Text>
			</Flex>
			<PopoverTextArea
				value={task.linkedTaskParameters?.diagnosisCodes?.join(", ") ?? ""}
				onSave={updateTaskDiagnosisCodes}
				placeholder="Diagnosis codes (comma-separated)"
				style={{ flex: 1, minWidth: 0 }}
			/>
		</div>
	);
};

type TaskDetailsPaneProps = {
	taskId: TaskId;
};

export function TaskDetailsPane({ taskId }: TaskDetailsPaneProps) {
	const task = useEntity("task", taskId);
	const loggedInUser = useLoggedInUser();

	const updateTaskStatus = useTransaction(
		(tx, status: Task["status"], unsnoozeDate: Date | undefined) => {
			tx.update("task", taskId, (task) => ({
				...task,
				status,
				unsnoozeDate,
				// Set ehrStatus to undefined when task is completed
				linkedTaskParameters:
					status === TaskStatus.Completed && task.linkedTaskParameters
						? {
								...task.linkedTaskParameters,
								ehrStatus: undefined as string | undefined,
							}
						: task.linkedTaskParameters,
			}));

			if (status === TaskStatus.Snoozed) {
				tx.set("activityLogItem", {
					id: tag<ActivityLogItemId>(crypto.randomUUID()),
					taskId,
					createdAt: new Date(),
					createdBy: loggedInUser?.id,
					activityType: ActivityType.Snooze,
					mediaItem: undefined,
					payload: {
						type: ActivityType.Snooze,
						unsnoozeDate: unsnoozeDate?.toISOString(),
					},
				});
			}
		},
	);

	const updateTaskTitle = useTransaction((tx, title: string) => {
		tx.update("task", taskId, (task) => ({
			...task,
			title,
		}));
	});

	const updateTaskSpecialty = useTransaction(
		(tx, specialtyId: SpecialtyId | undefined) => {
			tx.update("task", taskId, (task) => ({
				...task,
				specialtyId,
			}));
		},
	);

	if (!task) return null;

	// Determine if this is a referral task
	const isReferralTask = task.taskType === "Referral";

	return (
		<Flex
			column
			flex
			padding="xl"
			gap="xl"
			style={{
				overflowY: "auto",
				overflowX: "hidden",
			}}
		>
			<Flex justify="flex-end" gap="xs">
				{/* The mark needs a referral row to hang its milestone on. */}
				{isReferralTask && task.referralId && (
					<SentStatusPicker taskId={taskId} />
				)}
				<StatusPicker
					status={task.status}
					unsnoozeDate={task.unsnoozeDate}
					onChange={updateTaskStatus}
				/>
			</Flex>
			<Flex column gap="xs">
				<Flex align="center" gap="lg" justify="space-between">
					{isReferralTask ? (
						<SpecialtyPicker
							specialtyId={task.specialtyId}
							onChange={(id) =>
								updateTaskSpecialty(id as SpecialtyId | undefined)
							}
						/>
					) : (
						<Input
							size="xl"
							weight="semibold"
							value={task.title}
							onChange={(e) => updateTaskTitle(e.target.value)}
							aria-label="Task title"
							placeholder="Task title"
							style={{ flex: 1, marginRight: "16px" }}
						/>
					)}
				</Flex>
				{task.linkedTaskParameters?.ehrStatus && (
					<Flex align="center" gap="xs">
						<Icon name="TriangleAlert" size="sm" color="amber11" shrink={0} />
						<Text size="sm" color="sand11">
							{task.linkedTaskParameters.ehrStatus}
						</Text>
					</Flex>
				)}
			</Flex>
			<TaskAttributes taskId={taskId} />
			<PatientCard taskId={taskId} />
			<ActivityLog taskId={taskId} />
		</Flex>
	);
}
